import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<ActivationView />` — Smart Health Account activation flow.
 *
 * Drives the `IActivationFlow` orchestrator + dispatches step views
 * by current state per UC22 §1 (activation phase). Composes:
 *
 *   IIdentityProofingClient — NIST IAL2 vendor handoff (PFO §2.2)
 *   IPCIServiceClient       — substrate identity issuance (Tech Spec §4.1)
 *   IConsentArtifactClient  — activation-scope artifact under
 *                             patient-rights authority frame
 *
 * **Doctrinal posture.** Activation predates KEK release. No
 * IEnvelopeCryptoAdapter / IActiveSession is used in this flow;
 * those wire in at sign-in (claim-queue item 2). Patient session
 * crypto state begins after the patient signs into the active
 * Smart Health Account, not at activation.
 *
 * **Doctrinal-copy gate.** Patient-Facing Terms v1.3 (coord
 * issue #42) supplies the canonical activation language when v1.3
 * lands. Until then, step views ship placeholder copy aligned with
 * Tech Spec §6.1 / PFO §2.2 / §2.3 / UC22.
 *
 * **Composition entry point for tests + dev.** `<ActivationView />`
 * accepts the flow orchestrator as a prop so tests + dev can
 * supply mock-backed orchestrators. App-shell mounting will
 * construct the orchestrator with the production-bound clients
 * once production wiring lands.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { IntroStep } from './_activation/intro-step.js';
import { IdentityProofingStep } from './_activation/identity-proofing-step.js';
import { IdentityProofingRejectedStep } from './_activation/identity-proofing-rejected-step.js';
import { AccommodationStep } from './_activation/accommodation-step.js';
import { PCIProvisionedStep } from './_activation/pci-provisioned-step.js';
import { CompleteStep } from './_activation/complete-step.js';
import { PendingStep } from './_activation/pending-step.js';
import { CancelledOrErrorStep } from './_activation/cancelled-or-error-step.js';
function useActivationFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        const unsubscribe = flow.subscribe(setState);
        return unsubscribe;
    }, [flow]);
    return state;
}
export function ActivationView(props) {
    const state = useActivationFlowState(props.flow);
    React.useEffect(() => {
        if (state.kind === 'complete' && props.onComplete !== undefined) {
            props.onComplete();
        }
    }, [state, props.onComplete, props]);
    return (_jsx(ActivationViewBody, { state: state, props: props }));
}
function ActivationViewBody({ state, props, }) {
    const { t } = useLocale();
    return (_jsxs("section", { "aria-labelledby": "activation-heading", children: [_jsx("h2", { id: "activation-heading", children: t('activation.heading') }), renderStep(state, props, t)] }));
}
function renderStep(state, props, t) {
    const { flow } = props;
    const handleRestart = () => {
        if (props.onRestartRequested !== undefined) {
            props.onRestartRequested();
        }
    };
    switch (state.kind) {
        case 'intro':
            return (_jsx(IntroStep, { onAcknowledgeTerms: () => flow.acknowledgeTerms(), onCancel: () => flow.cancel('user-cancelled-at-intro') }));
        case 'terms-acknowledged':
            return (_jsx(IdentityProofingStep, { mode: { kind: 'pre-start' }, onStart: (opts) => {
                    void flow.startIdentityProofing(opts);
                }, onResolve: () => {
                    // Not reachable in pre-start mode; included so the
                    // prop shape is uniform.
                    void flow.resolveIdentityProofing();
                }, onCancel: () => flow.cancel('user-cancelled-at-identity-proofing-prompt') }));
        case 'identity-proofing-pending':
            return (_jsx(IdentityProofingStep, { mode: {
                    kind: 'pending',
                    sessionId: state.sessionId,
                    vendorRedirectUrl: state.vendorRedirectUrl,
                }, onStart: () => {
                    void flow.startIdentityProofing();
                }, onResolve: () => {
                    void flow.resolveIdentityProofing();
                }, onCancel: () => flow.cancel('user-cancelled-at-vendor-handoff') }));
        case 'identity-proofing-resolved':
            // Auto-advance: the verified resolution is a transient state;
            // the flow advances synchronously into pci-provisioning. Render
            // a brief pending indicator while the transition lands.
            void flow.continueToPCIProvisioning();
            return _jsx(PendingStep, { label: t('activation.pending.label.identity_setup') });
        case 'identity-proofing-rejected':
            return (_jsx(IdentityProofingRejectedStep, { onCancel: () => flow.cancel('identity-proofing-rejected-acknowledged') }));
        case 'accommodation-requested':
            return (_jsx(AccommodationStep, { accommodation: state.accommodation, onCancel: () => flow.cancel('accommodation-requested-acknowledged') }));
        case 'pci-provisioning-pending':
            return _jsx(PendingStep, { label: t('activation.pending.label.identity_setup') });
        case 'pci-provisioned':
            return (_jsx(PCIProvisionedStep, { onContinue: () => {
                    void flow.continueToConsentArtifactIssuance();
                }, onCancel: () => flow.cancel('user-cancelled-at-pci-provisioned') }));
        case 'consent-artifact-issuance-pending':
            return _jsx(PendingStep, { label: t('activation.pending.label.recording_activation') });
        case 'complete':
            return _jsx(CompleteStep, { artifact: state.artifact });
        case 'cancelled':
            return (_jsx(CancelledOrErrorStep, { kind: "cancelled", detail: state.reason, onRestart: handleRestart }));
        case 'error':
            return (_jsx(CancelledOrErrorStep, { kind: "error", detail: `${state.code}: ${state.message}`, onRestart: handleRestart }));
    }
}
//# sourceMappingURL=activation-view.js.map